import { TableEffects } from './effects'
import type { CameraState, EnvironmentPreset, RenderObject, TableRenderer, Vec3 } from '@v2/contracts'
import { actionTargets, selectionCommand, setControl, type ActionOption, type Projection } from '@v2/sdk'
import { TableFeedback, TABLE_CURSOR, ACTION_CURSOR } from './feedback'
import { TableAnimations } from './animation'
import { TableCamera, pointerOnPlane } from './camera'
import { CameraDirector } from './camera-director'
import { TableAudio } from './audio'
import type { InterfaceSound } from './sound-bank'
import { inferSceneSounds } from './scene-sounds'
import { CanvasInput } from './input'
import { EnvironmentPresentation, environmentStorage } from './environment'
import { compileScene } from './layout'
import { HandPresentation } from './hand'
import { resolveInspection } from './inspection'
import { styleTableSurface } from './surface'
import type { TableDebug, TableOptions, TableState, TableTrace } from './types'

/** Owns a single table's transient state, clock and adapters. No game-specific rules. */
export class TableController {
  private projection: Projection | null = null
  private objects: readonly RenderObject[] = []
  private currentFrame: readonly RenderObject[] = []
  private camera = new TableCamera()
  private director = new CameraDirector(this.camera)
  private audio = new TableAudio()
  private effects = new TableEffects()
  private animations = new TableAnimations()
  private hand = new HandPresentation()
  private handHoveredId: string | null = null
  private feedback = new TableFeedback()
  private tactileId: string | null = null
  private pointerHover: { id: string | null; point: { x: number; y: number } | null; camera: CameraState; motionTime: number } | null = null
  private renderedCamera: CameraState | null = null
  private renderedMotionTime = -1
  private lifts = new Map<string, number>()
  private actionsDisabled = false
  private input: CanvasInput
  private resizeObserver: ResizeObserver
  private disposed = false
  private animationFrame = 0
  private clock = 0
  private lastTick = 0
  private lastPublish = 0
  private drag: { id: string; position: Vec3 } | null = null
  private reducedMotion = matchMedia('(prefers-reduced-motion: reduce)')
  private environment = new EnvironmentPresentation(environmentStorage())
  private state: TableState

  constructor(private canvas: HTMLCanvasElement, private renderer: TableRenderer, private options: TableOptions) {
    this.state = {
      hoveredId: null, selectedId: null, inspectedId: null, peekedId: null, inspecting: false, menu: null, selection: null,
      hand: this.hand.state,
      gesture: 'idle', cameraMode: 'orbit', cameraOwner: 'overview', camera: this.camera.state,
      paused: false, revision: 0, objectCount: 0, stats: null, trace: [],
      environment: this.environment.settings, reducedMotion: this.reducedMotion.matches, soundsMuted: this.audio.muted,
    }
    this.feedback.globallyMuted = this.audio.muted
    canvas.addEventListener("pointerdown", this.feedback.unlock)
    canvas.style.cursor = TABLE_CURSOR
    this.input = new CanvasInput(canvas, {
      ping: point => {
        const rect = canvas.getBoundingClientRect()
        const id = this.renderer.pick(point.x, point.y, "inspect")
        if (id && this.hand.zoneFor(id)) return
        const object = this.currentFrame.find(object => object.id === id)
        const position = pointerOnPlane(this.camera.state, point.x, point.y, rect.width, rect.height, object ? object.position[1] + object.size[1] / 2 : 0.08)
        if (position && this.feedback.add(position)) this.options.onPing?.(position)
      },
      pick: (point, inspect = false) => {
        const readable = this.renderer.pick(point.x, point.y, 'inspect')
        const rect = canvas.getBoundingClientRect()
        const held = this.hand.pick(point.x, point.y, this.objects, rect.width, rect.height, readable, this.handHoveredId)
        return held ?? (inspect ? readable : this.renderer.pick(point.x, point.y, 'action') ?? readable)
      },
      draggable: id => !this.actionsDisabled && !this.state.inspectedId && Boolean(this.projection?.actions.some(action => actionTargets(action).includes(id) && action.dropZoneId)),
      inspectMode: inspecting => {
        if (this.state.inspecting === inspecting) return
        if (inspecting) this.director.interrupt()
        this.canvas.style.cursor = inspecting ? 'zoom-in' : TABLE_CURSOR
        this.change({ inspecting, hoveredId: null, ...(!inspecting ? { peekedId: null } : {}) })
      },
      hover: (id, inspect = false, point) => {
        if (!inspect && id && id !== this.tactileId && !this.state.inspectedId) {
          const piece = this.projection?.scene.pieces.find(piece => piece.id === id)
          if (piece && piece.kind !== 'board') this.audio.interaction(piece.kind === 'card' ? 'card-hover' : 'piece-hover')
        }
        this.tactileId = !inspect ? id : null
        this.pointerHover = !inspect && (id || point)
          ? { id, point: point ?? null, camera: this.renderedCamera ?? this.camera.state, motionTime: this.renderedMotionTime } : null
        this.handHoveredId = id && this.hand.zoneFor(id) ? id : null
        if (inspect) { this.peek(id); return }
        const hoveredId = id && this.isInteractable(id) ? id : null
        if (this.state.hoveredId === hoveredId) return
        this.canvas.style.cursor = hoveredId ? ACTION_CURSOR : TABLE_CURSOR
        this.change({ hoveredId })
      },
      click: (id, point, inspect) => {
        if (inspect) { this.peek(id); return }
        if (this.state.inspectedId) { this.inspect(null); return }
        if (!id || !this.projection || !this.isInteractable(id)) { this.closeMenu(); return }
        // A physical option of a pending set choice toggles its value instead of opening a menu.
        const toggles = this.actionsDisabled ? [] : this.projection.actions.flatMap(action => {
          const control = setControl(action)
          return control ? control.options.filter(option => option.targetId === id).map(option => ({ controlId: control.id, value: option.value })) : []
        })
        if (toggles.length === 1) { this.toggleSelection(toggles[0]!.controlId, toggles[0]!.value); this.closeMenu(); return }
        const direct = this.projection.actions.find(action => actionTargets(action).includes(id) && action.presentation === 'direct')
        if (direct && !this.actionsDisabled && !this.projection.scene.pieces.find(piece => piece.id === id)?.contents) { this.activate(direct.id); return }
        this.audio.interaction('select')
        this.change({ selectedId: id, menu: { objectId: id, x: point.x, y: point.y }, inspectedId: null })
        this.trace('menu', id)
      },
      drag: (id, point) => {
        const rect = canvas.getBoundingClientRect()
        const position = pointerOnPlane(this.camera.state, point.x, point.y, rect.width, rect.height, 0.65)
        if (position) {
          if (!this.drag) {
            const piece = this.projection?.scene.pieces.find(piece => piece.id === id)
            this.audio.interaction(piece?.kind === 'card' ? 'card-pickup' : 'pickup')
          }
          this.drag = { id, position }
        }
      },
      drop: (id, point) => {
        const rect = canvas.getBoundingClientRect()
        const position = pointerOnPlane(this.camera.state, point.x, point.y, rect.width, rect.height, 0.12)
        const actions = this.projection?.actions.filter(action => actionTargets(action).includes(id) && action.dropZoneId) ?? []
        const matches = actions.filter(action => {
          const zone = this.objects.find(object => object.id === action.dropZoneId)
          return zone && position && Math.abs(position[0] - zone.position[0]) <= zone.size[0] / 2 && Math.abs(position[2] - zone.position[2]) <= zone.size[2] / 2
        })
        this.drag = null
        // Restore the authoritative pose before another pointer or diagnostic query.
        this.draw(0)
        if (matches.length === 1) this.activate(matches[0].id)
        else this.trace('drop returned', id)
      },
      orbit: (dx, dy) => this.orbitCamera(dx, dy),
      pan: (dx, dy) => this.panCamera(dx, dy),
      zoom: (delta, point) => this.zoomCamera(delta, point),
      cancel: () => { this.drag = null; this.closeMenu(); this.inspect(null); this.peek(null); this.trace('cancel', 'Transient input cleared') },
      home: () => this.resetCamera(),
      top: () => this.topCamera(),
      place: shortcut => {
        const place = this.projection?.scene.camera?.places?.find(place => place.shortcut === shortcut)
        return place ? this.goToPlace(place.id) : false
      },
      gesture: gesture => {
        if (this.state.gesture === gesture) return
        if (gesture !== 'idle') {
          this.director.interrupt()
          this.tactileId = null
          this.pointerHover = null
        }
        this.change({ gesture, ...(gesture !== 'idle' ? { menu: null } : {}) })
        this.trace('gesture', gesture)
      },
    })
    this.resizeObserver = new ResizeObserver(this.resize)
    this.resizeObserver.observe(canvas)
    this.reducedMotion.addEventListener('change', this.motionPreferenceChanged)
    canvas.ownerDocument?.addEventListener('pointerdown', this.unlockAudio, true)
    canvas.ownerDocument?.addEventListener('keydown', this.unlockAudio, true)
    this.resize()
    this.animationFrame = requestAnimationFrame(this.tick)
  }

  update(projection: Projection): void {
    if (this.disposed) return
    const baseline = !this.projection || this.projection.viewer !== projection.viewer || this.projection.game.id !== projection.game.id || projection.revision < this.projection.revision
    // Socket status/HTTP refreshes may repeat the same private projection. They
    // must not dismiss a menu or restart a physical move already in progress.
    if (!baseline && this.projection?.revision === projection.revision && JSON.stringify(this.projection) === JSON.stringify(projection)) return
    this.input.reset()
    this.drag = null
    this.handHoveredId = null
    this.tactileId = null
    this.pointerHover = null
    this.lifts.clear()
    if (baseline) this.feedback.clear()
    const previous = this.projection
    this.projection = projection
    this.environment.configure(projection.scene.environment)
    this.objects = compileScene(projection)
    const hand = this.hand.update(projection, baseline)
    if (baseline || this.camera.owner === 'overview') this.frameOverview(baseline)
    this.director.update(projection.scene.camera, this.objects, this.clock, { baseline, aspect: this.aspect(), reducedMotion: this.reducedMotion.matches })
    this.effects.update(projection.scene.effects ?? [], this.clock, baseline)
    this.audio.update([...projection.scene.sounds ?? [], ...inferSceneSounds(previous, projection)], baseline)
    const rect = this.canvas.getBoundingClientRect()
    const destinations = this.hand.resolve(this.objects, this.camera.state, rect.width, rect.height, null, 0, true)
    this.animations.update(destinations, this.clock, baseline || this.reducedMotion.matches, { scene: projection.scene })
    this.change({ hand, revision: projection.revision, environment: this.environment.settings, camera: this.camera.state, cameraOwner: this.camera.owner, objectCount: this.objects.length, selectedId: null, hoveredId: null, inspectedId: null, peekedId: null, inspecting: false, menu: null, selection: null, gesture: 'idle', ...(baseline ? { trace: [] } : {}) })
    this.trace('projection', `Revision ${projection.revision}${baseline ? ' · baseline' : ''}`)
    this.draw(0)
    if (!baseline) this.input.refreshHover()
  }

  receivePing(position: Vec3): void { this.feedback.add(position) }
  getPingMuted(): boolean { return this.feedback.muted }
  setPingMuted(muted: boolean): void { this.feedback.muted = muted }

  activate(actionId: string): void {
    const action = this.projection?.actions.find(candidate => candidate.id === actionId)
    if (this.actionsDisabled || !action || !this.projection?.viewer) { this.trace('action unavailable', actionId); return }
    this.audio.interaction('confirm')
    this.closeMenu()
    this.trace('command', action.id)
    this.options.onAction(action)
  }

  /** Toggle one value of a set-controlled action. Values beyond the maximum replace the oldest choice. */
  toggleSelection(controlId: string, value: string): void {
    const action = this.projection?.actions.find(candidate => setControl(candidate)?.id === controlId)
    const control = action && setControl(action)
    if (!control || this.actionsDisabled || !control.options.some(option => option.value === value)) { this.trace('selection unavailable', `${controlId}:${value}`); return }
    const current = this.state.selection?.controlId === controlId ? this.state.selection.values : []
    let values = current.includes(value) ? current.filter(candidate => candidate !== value) : [...current, value]
    while (values.length > control.max) values = values.slice(1)
    this.audio.interaction('select')
    this.change({ selection: { controlId, values }, menu: null, selectedId: null })
    this.trace('selection', `${controlId}: ${values.join(', ') || 'none'}`)
    this.draw(0)
  }

  clearSelection(): void {
    if (this.state.selection) { this.change({ selection: null }); this.draw(0) }
  }

  /** The exact action and command for the current draft, once it satisfies the control. */
  selectedCommand(): { action: ActionOption; values: readonly string[] } | null {
    const selection = this.state.selection
    const action = selection && this.projection?.actions.find(candidate => setControl(candidate)?.id === selection.controlId)
    return action && selection && selectionCommand(action, selection.values) ? { action, values: selection.values } : null
  }

  /** Physical components whose set option is currently chosen. */
  private selectedTargets(): Set<string> {
    const selection = this.state.selection
    const action = selection && this.projection?.actions.find(candidate => setControl(candidate)?.id === selection.controlId)
    const control = action && setControl(action)
    return new Set(control ? control.options.filter(option => option.targetId && selection.values.includes(option.value)).map(option => option.targetId!) : [])
  }

  inspect(id: string | null): void {
    const piece = this.projection?.scene.pieces.find(item => item.id === id)
    if (piece) this.director.interrupt()
    const held = piece && this.hand.zoneFor(piece.id)
    if (held) this.hand.show()
    this.change({ inspectedId: piece?.id ?? null, menu: null, selectedId: null, hand: this.hand.state })
    if (piece) this.trace('inspect', piece.id)
    this.draw(0)
  }

  /** Temporary read-only inspection, shared by canvas and screen-space hand cards. */
  peek(id: string | null): void {
    const peekedId = this.projection?.scene.pieces.find(item => item.id === id)?.id ?? null
    if (this.state.peekedId === peekedId) return
    this.change({ peekedId, hoveredId: null })
  }

  private isInteractable(id: string): boolean {
    return !this.state.inspectedId && (Boolean(this.projection?.scene.pieces.find(piece => piece.id === id && !piece.hidden)?.contents) || (!this.actionsDisabled && Boolean(this.projection?.viewer && this.projection.actions.some(action => actionTargets(action).includes(id)))))
  }

  closeMenu(): void {
    if (this.state.menu || this.state.selectedId) this.change({ menu: null, selectedId: null })
  }

  focus(id: string): void {
    if (id === 'overview') { this.resetCamera(); return }
    const handZone = this.hand.zoneFor(id) ?? (this.hand.state.zone?.id === id ? id : undefined)
    if (handZone) { this.showHand(); return }
    const object = this.objects.find(candidate => candidate.id === id)
    if (!object) return
    this.camera.focus(object.position, object.size, this.aspect(), object.rotation)
    this.closeMenu()
    this.publishCamera()
    this.trace('focus', id)
  }

  resetCamera(): void {
    this.frameOverview()
    this.publishCamera()
    this.trace('camera', 'Overview')
  }

  /** SDK-authored destinations are navigation only, never game actions. */
  goToPlace(id: string): boolean {
    if (!this.director.goToPlace(id)) return false
    this.closeMenu()
    this.publishCamera()
    this.trace('camera place', id)
    return true
  }

  playInterfaceSound(sound: InterfaceSound): void { this.audio.interaction(sound) }
  playTurnSound(): void { this.audio.turn() }

  setSoundMuted(muted: boolean): void {
    this.audio.setMuted(muted)
    this.feedback.globallyMuted = muted
    if (!muted) void this.audio.unlock()
    this.change({ soundsMuted: this.audio.muted })
  }

  private unlockAudio = (event: Event): void => {
    if (event.isTrusted) void this.audio.unlock()
  }

  showHand(): void {
    if (!this.hand.state.zone) return
    this.hand.show()
    this.handHoveredId = null
    this.closeMenu(); this.peek(null)
    this.change({ hand: this.hand.state })
    this.draw(0)
    this.input.refreshHover()
  }
  toggleHand(): void {
    this.handHoveredId = null
    this.hand.toggle(); this.closeMenu(); this.peek(null)
    this.change({ hand: this.hand.state, hoveredId: null })
    this.draw(0)
    this.input.refreshHover()
  }
  setActionsDisabled(disabled: boolean): void {
    if (this.actionsDisabled === disabled) return
    this.actionsDisabled = disabled
    if (disabled) { this.input.reset(); this.drag = null; this.change({ hoveredId: null }); this.canvas.style.cursor = TABLE_CURSOR }
    this.draw(0)
    this.input.refreshHover()
  }

  setCameraMode(mode: 'orbit' | 'pan'): void {
    this.input.setMode(mode)
    this.change({ cameraMode: mode })
  }
  panCamera(dx: number, dy: number): void {
    this.closeMenu()
    this.camera.pan(dx, dy, this.canvas.getBoundingClientRect().height)
    this.publishCamera()
  }
  orbitCamera(dx: number, dy: number): void {
    this.closeMenu()
    this.camera.orbit(dx, dy)
    this.publishCamera()
  }
  zoomCamera(delta: number, point?: { x: number; y: number }): void {
    this.closeMenu()
    const rect = this.canvas.getBoundingClientRect()
    this.camera.zoom(delta, point ? { ...point, width: rect.width, height: rect.height } : undefined)
    this.publishCamera()
  }
  topCamera(): void {
    this.closeMenu()
    this.camera.topView()
    this.publishCamera()
  }

  setPaused(paused: boolean): void { this.change({ paused }); this.trace('clock', paused ? 'Paused' : 'Running') }
  setEnvironment(preset: EnvironmentPreset): void {
    this.environment.setPreset(preset)
    this.change({ environment: this.environment.settings })
    this.draw(0)
  }
  setEnvironmentMotion(enabled: boolean): void {
    this.environment.setMotion(enabled)
    this.change({ environment: this.environment.settings })
    this.draw(0)
  }
  step(milliseconds = 16.667): void {
    if (!this.state.paused || !Number.isFinite(milliseconds) || milliseconds <= 0) return
    this.clock += Math.min(milliseconds, 1000) / 1000
    this.environment.advance(Math.min(milliseconds, 1000) / 1000, this.reducedMotion.matches)
    this.draw(milliseconds / 1000)
    this.publishCamera()
  }
  getState(): TableState { return this.state }
  /** Read-only pointer hover, including inert piles, without publishing pointer motion to React. */
  hoveredPieceId(): string | null {
    const hover = this.pointerHover
    if (this.disposed || !hover || this.state.gesture !== 'idle'
      || this.state.inspecting || this.state.peekedId || this.state.inspectedId || this.state.menu) return null
    const before = hover.camera, now = this.renderedCamera ?? this.camera.state
    // Recheck only after camera or piece motion. A drawn card can pass over a
    // stack before arriving in the hand, even when the pointer remains still.
    if (hover.motionTime !== this.renderedMotionTime
      || Math.abs(before.azimuth - now.azimuth) > 0.0001 || Math.abs(before.elevation - now.elevation) > 0.0001
      || Math.abs(before.distance - now.distance) > 0.0001 || before.fov !== now.fov
      || before.target.some((value, index) => Math.abs(value - now.target[index]!) > 0.0001)) {
      hover.id = hover.point ? this.renderer.pick(hover.point.x, hover.point.y, 'inspect') : null
      hover.camera = now
      hover.motionTime = this.renderedMotionTime
    }
    return hover.id && !this.hand.zoneFor(hover.id)
      && this.projection?.scene.pieces.some(piece => piece.id === hover.id) ? hover.id : null
  }
  debug(): TableDebug { return { state: this.state, projection: this.projection, objects: this.currentFrame, stats: this.renderer.stats(), environment: { preset: this.environment.settings.preset, time: this.environment.time } } }
  effectLabels(): { id: string; text: string; color: string; x: number; y: number; opacity: number }[] {
    const lanes = new Map<string, number>()
    return this.effects.sample(this.clock).filter(effect => effect.cue.kind === 'label').flatMap(({ cue, progress }) => {
      const point = this.screenPoint(cue.targetId) ?? (cue.fallbackTargetId ? this.screenPoint(cue.fallbackTargetId) : null)
      if (!point || !cue.text) return []
      const lane = lanes.get(cue.targetId) ?? 0
      lanes.set(cue.targetId, lane + 1)
      return [{ id: cue.id, text: cue.text, color: cue.color ?? '#ffe6a3', x: point.x,
        y: point.y - 32 - (this.reducedMotion.matches ? lane * 40 : progress * 140), opacity: Math.min(1, (1 - progress) * 5) }]
    })
  }
  screenPoint(id: string, options: { hideBehindHand?: boolean } = {}): { x: number; y: number } | null {
    const point = this.renderer.project(id, options)
    const rect = this.canvas.getBoundingClientRect()
    return point ? { x: rect.left + point[0], y: rect.top + point[1] } : null
  }

  private aspect(): number { const rect = this.canvas.getBoundingClientRect(); return rect.width / Math.max(1, rect.height) }
  private frameOverview(immediate = false): void {
    const surface = this.objects.find(object => object.id === '__table:wood')
    const clearance = this.hand.state.zone ? (surface?.size[2] ?? 12) * 0.3 : 0
    this.camera.overview(this.aspect(), immediate, surface ? { center: [surface.position[0], 0, surface.position[2] + clearance / 2], width: surface.size[0], depth: surface.size[2] + clearance } : undefined)
  }
  private resize = (): void => {
    if (this.disposed) return
    const rect = this.canvas.getBoundingClientRect()
    this.renderer.resize(Math.max(1, rect.width), Math.max(1, rect.height), Math.min(devicePixelRatio || 1, 2))
    if (this.camera.owner === 'overview') this.frameOverview(true)
    if (this.projection) this.director.update(this.projection.scene.camera, this.objects, this.clock, { baseline: false, aspect: this.aspect(), reducedMotion: this.reducedMotion.matches })
  }

  private change(patch: Partial<TableState>): void {
    this.state = { ...this.state, ...patch }
    this.options.onState?.(this.state)
  }
  private trace(event: string, detail: string): void {
    const entry: TableTrace = { at: Math.round(this.clock * 1000), event, detail }
    this.change({ trace: [...this.state.trace.slice(-23), entry] })
  }
  private publishCamera(): void { this.change({ camera: this.camera.state, cameraOwner: this.camera.owner, stats: this.renderer.stats() }) }
  private motionPreferenceChanged = (): void => {
    this.change({ reducedMotion: this.reducedMotion.matches })
    if (this.projection) this.director.update(this.projection.scene.camera, this.objects, this.clock, { baseline: false, aspect: this.aspect(), reducedMotion: this.reducedMotion.matches })
    if (this.reducedMotion.matches && this.projection) {
      const rect = this.canvas.getBoundingClientRect()
      this.animations.update(this.hand.resolve(this.objects, this.camera.state, rect.width, rect.height, null, 0, true), this.clock, true, { scene: this.projection.scene })
    }
    this.draw(0)
  }

  private tick = (timestamp: number): void => {
    if (this.disposed) return
    const seconds = this.lastTick ? Math.min(0.05, Math.max(0, (timestamp - this.lastTick) / 1000)) : 0
    this.lastTick = timestamp
    if (!this.state.paused) this.clock += seconds
    if (!document.hidden) {
      if (!this.state.paused) this.environment.advance(seconds, this.reducedMotion.matches)
      this.draw(this.state.paused ? 0 : seconds)
      if (timestamp - this.lastPublish > 600) { this.lastPublish = timestamp; this.publishCamera() }
    }
    this.animationFrame = requestAnimationFrame(this.tick)
  }

  private draw(seconds: number): void {
    this.director.tick(this.clock)
    const camera: CameraState = this.camera.tick(seconds)
    const rect = this.canvas.getBoundingClientRect()
    const destinations = this.hand.resolve(this.objects, camera, rect.width, rect.height, this.handHoveredId ?? this.state.selectedId, seconds, this.reducedMotion.matches)
    const chosen = this.selectedTargets()
    if (this.animations.pending) this.renderedMotionTime = this.clock
    this.currentFrame = this.animations.sample(this.clock, destinations).filter(object => this.hand.visible(object)).map(source => {
      const active = this.actionsDisabled ? { ...source, interactive: false, highlighted: false } : source
      const emphasized = active.layer === 'foreground' && !active.decorative && this.projection?.scene.payment && this.projection.viewer === this.projection.activePlayer
        ? { ...active, dimmed: !active.interactive } : active
      const object = (this.projection?.scene.payment?.targetId === active.id || this.projection?.scene.payment?.targetIds?.includes(active.id))
        ? { ...emphasized, paymentEmphasis: this.reducedMotion.matches ? 1 : 0.55 + 0.45 * Math.sin(this.clock * 3) ** 2 }
        : emphasized
      if (this.drag?.id === object.id) {
        const physical = this.objects.find(candidate => candidate.id === object.id)!
        return { ...object, position: this.drag.position, orientation: undefined, layer: undefined, size: physical.size, highlighted: true }
      }
      const hover = object.interactive && this.isInteractable(object.id) && (this.state.hoveredId === object.id || this.state.selectedId === object.id || chosen.has(object.id))
      const piece = this.projection?.scene.pieces.find(piece => piece.id === object.id)
      const zone = piece && this.projection?.scene.zones.find(zone => zone.id === piece.zone)
      const tactile = piece && piece.kind !== 'board' && (piece.count !== undefined || zone?.role === 'deck' || zone?.layout === 'stack' || zone?.role === 'supply')
      const target = object.layer !== 'foreground' && ((hover && object.shape === 'card') || (tactile && this.tactileId === object.id)) ? 0.14 : 0
      const before = this.lifts.get(object.id) ?? 0
      const lift = this.reducedMotion.matches ? target : before + (target - before) * (1 - Math.exp(-seconds * 16))
      this.lifts.set(object.id, lift)
      if (lift > 0.001) return { ...object, position: [object.position[0], object.position[1] + lift, object.position[2]], highlighted: Boolean(hover) }

      return styleTableSurface(hover ? { ...object, highlighted: true } : object, this.environment.settings.preset)
    })
    this.currentFrame = [...this.currentFrame, ...this.feedback.sample(this.reducedMotion.matches), ...this.effects.rings(this.clock, this.objects, this.reducedMotion.matches)]
    const inspection = resolveInspection(this.currentFrame, this.state.peekedId ?? this.state.inspectedId, camera, rect.width, rect.height)
    if (inspection) this.currentFrame = [...this.currentFrame, inspection]
    const surface = this.objects.find(object => object.id === '__table:wood')
    this.renderer.draw({ objects: this.currentFrame, camera, time: this.clock, environment: {
      preset: this.environment.settings.preset, time: this.environment.time,
      bounds: surface ? { center: [surface.position[0], 0, surface.position[2]], width: surface.size[0], depth: surface.size[2] } : undefined,
    } })
    this.renderedCamera = camera
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    cancelAnimationFrame(this.animationFrame)
    this.resizeObserver.disconnect()
    this.reducedMotion.removeEventListener('change', this.motionPreferenceChanged)
    this.canvas.ownerDocument?.removeEventListener('pointerdown', this.unlockAudio, true)
    this.canvas.ownerDocument?.removeEventListener('keydown', this.unlockAudio, true)
    this.input.dispose()
    this.director.clear()
    this.audio.dispose()
    this.canvas.removeEventListener("pointerdown", this.feedback.unlock)
    this.feedback.dispose()
    this.effects.clear()
    this.animations.clear()
    this.hand.clear()
    this.handHoveredId = null
    this.tactileId = null
    this.pointerHover = null
    this.renderer.dispose()
    this.projection = null
    this.objects = []
    this.currentFrame = []
  }
}
